export function registerVlHomeHeroVideo(win = window) {
  if (win.customElements.get('vl-home-hero-video')) return;
  class VlHomeHeroVideo extends win.HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.controller = new win.AbortController();
      const {signal} = this.controller;
      this.mount = this.querySelector('[data-hero-video-mount]');
      this.button = this.querySelector('[data-hero-video-toggle]');
      this.desktop = this.querySelector('template[data-hero-video="desktop"]');
      this.mobile = this.querySelector('template[data-hero-video="mobile"]') || this.desktop;
      if (!this.mount || !this.button || !this.desktop) return;
      this.breakpoint = win.matchMedia('(max-width: 900px)');
      this.motion = win.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.userStarted = false;
      this.visible = !win.IntersectionObserver;
      this.button.hidden = false;
      this.updateButton(false);
      this.button.addEventListener('click', () => {
        if (this.video && (!this.video.paused || this.playingRequest?.video === this.video)) {
          this.userPaused = true;
          this.userStarted = false;
          this.pause();
        } else {
          this.userPaused = false;
          this.userStarted = true;
          this.sync();
        }
      }, {signal});
      this.breakpoint.addEventListener('change', () => this.sync(), {signal});
      this.motion.addEventListener('change', () => {
        this.userStarted = false;
        this.sync();
      }, {signal});
      win.document.addEventListener('visibilitychange', () => this.sync(), {signal});
      if (win.IntersectionObserver) {
        this.observer = new win.IntersectionObserver(entries => {
          this.visible = entries.some(entry => entry.isIntersecting);
          this.sync();
        });
        this.observer.observe(this.closest('.vl-home-hero') || this);
      }
      this.sync();
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.unload();
      if (this.button) this.button.hidden = true;
    }
    shouldPlay() {
      const autoplayAllowed = !this.breakpoint.matches || this.dataset.mobileAutoplay !== 'false';
      return this.visible && win.document.visibilityState !== 'hidden' && !this.userPaused && ((!this.motion.matches && autoplayAllowed) || this.userStarted);
    }
    sync() {
      if (!this.controller || this.controller.signal.aborted) return;
      const template = this.breakpoint.matches ? this.mobile : this.desktop;
      if (this.source && this.source !== template) this.unload();
      if (!this.shouldPlay()) { this.pause(); return; }
      if (!this.video) {
        const video = template.content.querySelector('video')?.cloneNode(true);
        if (!video) return;
        this.source = template;
        this.video = video;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        // Safari can leave play() pending with preload=none on newly mounted media.
        // Only the selected, eligible video is mounted, so inactive sources stay inert.
        video.preload = 'metadata';
        // The responsive picture supplies the loading and error fallback.
        video.removeAttribute('poster');
        video.setAttribute('aria-hidden', 'true');
        video.tabIndex = -1;
        const {signal} = this.controller;
        video.addEventListener('playing', () => {
          if (this.video !== video) return;
          this.setAttribute('data-has-frame', '');
          this.updateButton(true);
        }, {signal});
        video.addEventListener('pause', () => {
          if (this.video === video) this.updateButton(false);
        }, {signal});
        video.addEventListener('error', () => {
          if (this.video !== video) return;
          this.userPaused = true;
          this.unload();
        }, {signal});
        this.mount.append(video);
      }
      const video = this.video;
      if (!video.paused || this.playingRequest?.video === video) return;
      const attempt = {video, interrupted: false, retryWhenEligible: false};
      this.playingRequest = attempt;
      Promise.resolve(video.play()).then(() => {
        if (this.video !== video || this.playingRequest !== attempt || !this.shouldPlay()) video.pause();
      }).catch(error => {
        if (this.video !== video || this.playingRequest !== attempt) return;
        // Our offscreen/document pause can abort a still-loading play request.
        // Only that known interruption can resume; browser denials stay paused.
        attempt.retryWhenEligible = attempt.interrupted && error?.name === 'AbortError';
        if (!attempt.retryWhenEligible) this.userPaused = true;
        this.updateButton(false);
      }).finally(() => {
        if (this.playingRequest !== attempt) return;
        this.playingRequest = null;
        // Visibility may have returned before the rejected request settled.
        if (attempt.retryWhenEligible && this.shouldPlay()) this.sync();
      });
    }
    pause() {
      if (this.playingRequest && this.playingRequest.video === this.video) this.playingRequest.interrupted = true;
      this.video?.pause();
      this.updateButton(false);
    }
    unload() {
      const video = this.video;
      this.video = null;
      this.source = null;
      this.playingRequest = null;
      if (video) {
        video.pause();
        video.removeAttribute('src');
        for (const source of video.querySelectorAll('source')) source.removeAttribute('src');
        video.load();
        video.remove();
      }
      this.removeAttribute('data-has-frame');
      this.updateButton(false);
    }
    updateButton(playing) {
      if (!this.button) return;
      this.button.setAttribute('aria-label', playing ? this.button.dataset.pauseLabel : this.button.dataset.playLabel);
      this.button.querySelector('[data-hero-video-play]')?.toggleAttribute('hidden', playing);
      this.button.querySelector('[data-hero-video-pause]')?.toggleAttribute('hidden', !playing);
    }
  }
  win.customElements.define('vl-home-hero-video', VlHomeHeroVideo);
}
if (typeof window !== 'undefined' && window.customElements) registerVlHomeHeroVideo(window);
